import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getConfig } from '../../../utils/getConfig';
import { registerFrame } from '../../../utils/dispatcher';

const Frame = styled.iframe`
  display: block;
  position: ${(props) => (props.fullView ? 'relative' : 'absolute')};
  border-width: 0;
`;

function PreviewFrame({ fullView, isOverlayVisible }) {
  const iframe = useRef();
  const [frameSize, setFrameSize] = useState(null);
  // PREVIEW_URL is only set for production (the separate preview Hosting site). In dev the same
  // server hosts both pages, so fall back to a RELATIVE /preview.html: hardcoding a host (e.g.
  // localhost:8000) breaks the moment the editor is opened via its LAN IP — the phone/AR use case —
  // because editor and preview then disagree about each other's origin and postMessage drops.
  const previewUrl =
    getConfig('PREVIEW_URL', { warn: false }) ?? '/preview.html';
  // postMessage's targetOrigin must be a bare origin (scheme://host:port); a value carrying a path
  // (e.g. PREVIEW_URL = "https://host/preview.html") never matches the frame's origin, so messages
  // are silently dropped and the sketch never renders. Derive the origin while keeping the full URL
  // as the iframe `src`. Resolving against window.location also handles the relative dev fallback.
  const previewOrigin = (() => {
    try {
      return new URL(previewUrl, window.location.href).origin;
    } catch {
      return '*';
    }
  })();
  useEffect(() => {
    const unsubscribe = registerFrame(
      iframe.current.contentWindow,
      previewOrigin
    );
    return () => {
      unsubscribe();
    };
  });

  useEffect(() => {
    const parent = iframe.current?.parentElement;
    if (!parent) {
      return () => {};
    }
    const updateSize = () => {
      const { width, height } = parent.getBoundingClientRect();
      setFrameSize({
        width: Math.floor(width),
        height: Math.floor(height)
      });
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(parent);
    return () => {
      observer.disconnect();
    };
  }, []);

  const frameUrl = previewUrl;
  const sandboxAttributes = `allow-forms allow-modals allow-pointer-lock allow-popups 
    allow-same-origin allow-scripts allow-top-navigation-by-user-activation allow-downloads`;
  const allow = `accelerometer; ambient-light-sensor; autoplay; bluetooth; camera; encrypted-media; geolocation; gyroscope; \
    hid; microphone; magnetometer; midi; payment; usb; serial; vr; xr-spatial-tracking`;

  return (
    <>
      <div
        className="preview-frame-overlay"
        style={{ display: isOverlayVisible ? 'block' : 'none' }}
      />
      <Frame
        title="sketch preview"
        src={frameUrl}
        sandbox={sandboxAttributes}
        allow={allow}
        scrolling="auto"
        allowtransparency
        allowpaymentrequest
        allowFullScreen
        frameBorder="0"
        ref={iframe}
        fullView={fullView}
        style={
          frameSize
            ? { width: frameSize.width, height: frameSize.height }
            : undefined
        }
      />
    </>
  );
}

PreviewFrame.propTypes = {
  fullView: PropTypes.bool,
  isOverlayVisible: PropTypes.bool
};

PreviewFrame.defaultProps = {
  fullView: false,
  isOverlayVisible: false
};

export default PreviewFrame;
